import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Switch } from '../ui/switch';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { Alert, AlertDescription } from '../ui/alert';
import { useToast } from '../ui/use-toast';
import api from '../../services/api';
import {
  Plus,
  Edit,
  Trash2,
  Link,
  FileText,
  Users,
  BookOpen,
  Layout,
  Globe,
  AlertCircle
} from 'lucide-react';

const iconOptions = [
  { value: 'link', label: 'Link', Icon: Link },
  { value: 'file-text', label: 'File Text', Icon: FileText },
  { value: 'users', label: 'Users', Icon: Users },
  { value: 'book-open', label: 'Book Open', Icon: BookOpen },
  { value: 'layout', label: 'Layout', Icon: Layout },
  { value: 'globe', label: 'Globe', Icon: Globe }
];

const AdminLinks = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sectionDialog, setSectionDialog] = useState({ open: false, mode: 'create', data: null });
  const [linkDialog, setLinkDialog] = useState({ open: false, sectionId: null, link: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: '', id: null, sectionId: null });
  const { toast } = useToast();

  // Form states
  const [sectionForm, setSectionForm] = useState({
    sectionTitle: '',
    sectionIcon: 'link',
    isActive: true
  });

  const [linkForm, setLinkForm] = useState({
    title: '',
    description: '',
    url: '',
    order: ''
  });

  useEffect(() => {
    fetchResourceLinks();
  }, []);

  const fetchResourceLinks = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/resource-links');
      setSections(response.data.data || []);
    } catch (error) {
      console.error('Error fetching resource links:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch resource links',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSection = () => {
    setSectionForm({
      sectionTitle: '',
      sectionIcon: 'link',
      isActive: true
    });
    setSectionDialog({ open: true, mode: 'create', data: null });
  };

  const handleEditSection = (section) => {
    setSectionForm({
      sectionTitle: section.sectionTitle,
      sectionIcon: section.sectionIcon,
      isActive: section.isActive
    });
    setSectionDialog({ open: true, mode: 'edit', data: section });
  };

  const handleSaveSection = async () => {
    try {
      if (sectionDialog.mode === 'create') {
        await api.post('/admin/resource-links', sectionForm);
        toast({
          title: 'Success',
          description: 'Section created successfully'
        });
      } else {
        await api.put(`/admin/resource-links/${sectionDialog.data._id}`, sectionForm);
        toast({
          title: 'Success',
          description: 'Section updated successfully'
        });
      }
      setSectionDialog({ open: false, mode: 'create', data: null });
      fetchResourceLinks();
    } catch (error) {
      console.error('Error saving section:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to save section',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteSection = async () => {
    try {
      await api.delete(`/admin/resource-links/${deleteDialog.id}`);
      toast({
        title: 'Success',
        description: 'Section deleted successfully'
      });
      setDeleteDialog({ open: false, type: '', id: null, sectionId: null });
      fetchResourceLinks();
    } catch (error) {
      console.error('Error deleting section:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete section',
        variant: 'destructive'
      });
    }
  };

  const handleAddLink = (sectionId) => {
    setLinkForm({
      title: '',
      description: '',
      url: '',
      order: ''
    });
    setLinkDialog({ open: true, sectionId, link: null });
  };

  const handleEditLink = (sectionId, link) => {
    setLinkForm({
      title: link.title,
      description: link.description,
      url: link.url,
      order: link.order || ''
    });
    setLinkDialog({ open: true, sectionId, link });
  };

  const handleSaveLink = async () => {
    try {
      const section = sections.find(s => s._id === linkDialog.sectionId);
      const updatedLinks = [...(section.links || [])];

      if (linkDialog.link) {
        // Edit existing link
        const linkIndex = updatedLinks.findIndex(l => l._id === linkDialog.link._id);
        if (linkIndex > -1) {
          updatedLinks[linkIndex] = { ...linkDialog.link, ...linkForm };
        }
      } else {
        // Add new link
        const order = linkForm.order || updatedLinks.length + 1;
        updatedLinks.push({ ...linkForm, order });
      }

      // Sort links by order
      updatedLinks.sort((a, b) => (a.order || 0) - (b.order || 0));

      await api.put(`/admin/resource-links/${linkDialog.sectionId}`, {
        links: updatedLinks
      });

      toast({
        title: 'Success',
        description: linkDialog.link ? 'Link updated successfully' : 'Link added successfully'
      });
      setLinkDialog({ open: false, sectionId: null, link: null });
      fetchResourceLinks();
    } catch (error) {
      console.error('Error saving link:', error);
      toast({
        title: 'Error',
        description: 'Failed to save link',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteLink = async () => {
    try {
      const section = sections.find(s => s._id === deleteDialog.sectionId);
      const updatedLinks = section.links.filter(l => l._id !== deleteDialog.id);

      await api.put(`/admin/resource-links/${deleteDialog.sectionId}`, {
        links: updatedLinks
      });

      toast({
        title: 'Success',
        description: 'Link deleted successfully'
      });
      setDeleteDialog({ open: false, type: '', id: null, sectionId: null });
      fetchResourceLinks();
    } catch (error) {
      console.error('Error deleting link:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete link',
        variant: 'destructive'
      });
    }
  };

  const handleToggleActive = async (sectionId, currentStatus) => {
    try {
      await api.put(`/admin/resource-links/${sectionId}`, {
        isActive: !currentStatus
      });
      toast({
        title: 'Success',
        description: 'Section status updated'
      });
      fetchResourceLinks();
    } catch (error) {
      console.error('Error toggling section status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update section status',
        variant: 'destructive'
      });
    }
  };

  const getIconComponent = (iconName) => {
    const iconOption = iconOptions.find(opt => opt.value === iconName);
    return iconOption ? iconOption.Icon : Link;
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Resource Links Management</CardTitle>
              <CardDescription>Manage resource links and sections visible to companies</CardDescription>
            </div>
            <Button onClick={handleCreateSection}>
              <Plus className="h-4 w-4 mr-2" />
              Add Section
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {sections.length === 0 ? (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                No resource link sections found. Click "Add Section" to create your first section.
              </AlertDescription>
            </Alert>
          ) : (
            <Accordion type="single" collapsible className="w-full space-y-2">
              {sections.map((section) => {
                const IconComponent = getIconComponent(section.sectionIcon);
                return (
                  <AccordionItem key={section._id} value={section._id} className="border rounded-lg">
                    <AccordionTrigger className="px-4">
                      <div className="flex items-center gap-4 flex-1">
                        <IconComponent className="h-5 w-5" />
                        <span className="font-medium">{section.sectionTitle}</span>
                        <Badge variant={section.isActive ? 'success' : 'secondary'}>
                          {section.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                        <Badge variant="outline">{section.links?.length || 0} links</Badge>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4">
                      <div className="space-y-4">
                        <div className="flex gap-2 pb-2 border-b">
                          <Button size="sm" onClick={() => handleAddLink(section._id)}>
                            <Plus className="h-4 w-4 mr-1" />
                            Add Link
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleEditSection(section)}>
                            <Edit className="h-4 w-4 mr-1" />
                            Edit Section
                          </Button>
                          <div className="flex items-center gap-2 ml-auto">
                            <Label htmlFor={`active-${section._id}`} className="text-sm">
                              Active
                            </Label>
                            <Switch
                              id={`active-${section._id}`}
                              checked={section.isActive}
                              onCheckedChange={() => handleToggleActive(section._id, section.isActive)}
                            />
                          </div>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => setDeleteDialog({
                              open: true,
                              type: 'section',
                              id: section._id,
                              sectionId: null
                            })}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete
                          </Button>
                        </div>

                        {section.links && section.links.length > 0 ? (
                          <div className="space-y-2">
                            {section.links.map((link) => (
                              <div key={link._id} className="flex items-start justify-between p-3 rounded-lg border bg-background/50">
                                <div className="flex-1">
                                  <h4 className="font-medium">{link.title}</h4>
                                  <p className="text-sm text-muted-foreground mt-1">{link.description}</p>
                                  <a
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-primary hover:underline mt-1 inline-block"
                                  >
                                    {link.url}
                                  </a>
                                </div>
                                <div className="flex gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleEditLink(section._id, link)}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setDeleteDialog({
                                      open: true,
                                      type: 'link',
                                      id: link._id,
                                      sectionId: section._id
                                    })}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <Alert>
                            <AlertDescription>
                              No links in this section. Click "Add Link" to add your first link.
                            </AlertDescription>
                          </Alert>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          )}
        </CardContent>
      </Card>

      {/* Section Dialog */}
      <Dialog open={sectionDialog.open} onOpenChange={(open) => !open && setSectionDialog({ open: false, mode: 'create', data: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {sectionDialog.mode === 'create' ? 'Create New Section' : 'Edit Section'}
            </DialogTitle>
            <DialogDescription>
              {sectionDialog.mode === 'create'
                ? 'Add a new resource section for companies to access'
                : 'Update the section details'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="sectionTitle">Section Title</Label>
              <Input
                id="sectionTitle"
                value={sectionForm.sectionTitle}
                onChange={(e) => setSectionForm({ ...sectionForm, sectionTitle: e.target.value })}
                placeholder="Enter section title"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sectionIcon">Section Icon</Label>
              <select
                id="sectionIcon"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={sectionForm.sectionIcon}
                onChange={(e) => setSectionForm({ ...sectionForm, sectionIcon: e.target.value })}
              >
                {iconOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="isActive"
                checked={sectionForm.isActive}
                onCheckedChange={(checked) => setSectionForm({ ...sectionForm, isActive: checked })}
              />
              <Label htmlFor="isActive">Active</Label>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setSectionDialog({ open: false, mode: 'create', data: null })}>
              Cancel
            </Button>
            <Button onClick={handleSaveSection}>
              {sectionDialog.mode === 'create' ? 'Create' : 'Update'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Link Dialog */}
      <Dialog open={linkDialog.open} onOpenChange={(open) => !open && setLinkDialog({ open: false, sectionId: null, link: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {linkDialog.link ? 'Edit Link' : 'Add New Link'}
            </DialogTitle>
            <DialogDescription>
              {linkDialog.link ? 'Update the link details' : 'Add a new resource link to this section'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="linkTitle">Link Title</Label>
              <Input
                id="linkTitle"
                value={linkForm.title}
                onChange={(e) => setLinkForm({ ...linkForm, title: e.target.value })}
                placeholder="Enter link title"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="linkDescription">Description</Label>
              <textarea
                id="linkDescription"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={linkForm.description}
                onChange={(e) => setLinkForm({ ...linkForm, description: e.target.value })}
                placeholder="Enter description"
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="linkUrl">URL</Label>
              <Input
                id="linkUrl"
                type="url"
                value={linkForm.url}
                onChange={(e) => setLinkForm({ ...linkForm, url: e.target.value })}
                placeholder="https://example.com"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="linkOrder">Order (Optional)</Label>
              <Input
                id="linkOrder"
                type="number"
                value={linkForm.order}
                onChange={(e) => setLinkForm({ ...linkForm, order: e.target.value })}
                placeholder="Leave blank to add at the end"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setLinkDialog({ open: false, sectionId: null, link: null })}>
              Cancel
            </Button>
            <Button onClick={handleSaveLink}>
              {linkDialog.link ? 'Update' : 'Add'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, type: '', id: null, sectionId: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this {deleteDialog.type}?
              {deleteDialog.type === 'section' && ' This will delete all links in this section.'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, type: '', id: null, sectionId: null })}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={deleteDialog.type === 'section' ? handleDeleteSection : handleDeleteLink}
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminLinks;